import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FullWidthCarousel from 'LuxKit/Carousel/FullWidthCarousel'
import HotelPageSearchHotelCategoryTile from './HotelPageSearchHotelCategoryTile'
import type { CategoryData } from './types'
import styled from 'styled-components'
import { rem } from 'polished'
import { ANYWHERE_NAME, ANYWHERE_PLACE_ID } from 'constants/search'

const Root = styled.div`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

function HotelPageDestinationTiles() {
  const intl = useIntl()

  const headingText = useMemo(() => {
    return intl.formatMessage(
      {
        id: '+LXFx8Gjel',
        defaultMessage: 'Discover <i>your</i> ideal escape',
        description: 'Heading for hotel categories carousel on the hotel or room booking page',
      },
      {
        i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
      },
    )
  }, [intl])

  const categories: Array<CategoryData> = useMemo(() => [
    {
      id: 'city_break',
      name: intl.formatMessage({
        id: 'IjIkhqZvgu',
        defaultMessage: 'City breaks',
        description: 'Category name for city breaks in the hotel type filter or category carousel',
      }),
      imageId: 'hotel-category-tile-city-break',
      show: true,
      parameterName: 'holidayTypes',
      parameterValue: 'City Break',
    },
    {
      id: 'sun_beach',
      name: intl.formatMessage({
        id: '/qnpZHqcug',
        defaultMessage: 'Sun & Beach',
        description: 'Category name for sun and beach properties in the hotel type filter or carousel',
      }),
      imageId: 'hotel-category-tile-sun-beach',
      show: true,
      parameterName: 'holidayTypes',
      parameterValue: 'Sun & Beach',
    },
    {
      id: 'private_pool',
      name: intl.formatMessage({
        id: 'Z0RzjYAhwl',
        defaultMessage: 'Private pool',
        description: 'Category name for private pool properties in the hotel type filter or carousel',
      }),
      imageId: 'hotel-category-tile-private-pool',
      show: true,
      parameterName: 'amenities',
      parameterValue: 'Private pool',
    },
    {
      id: 'unique_stays',
      name: intl.formatMessage({
        id: 'QwfFHbZeFx',
        defaultMessage: 'Unique stays',
        description: 'Category name for unique stays in the hotel type filter or category carousel',
      }),
      imageId: 'hotel-category-unique-stays',
      show: true,
      parameterName: 'holidayTypes',
      parameterValue: 'Unique Stays',
    },
    {
      id: 'spa_break',
      name: intl.formatMessage({
        id: 'aQiPdOq1vh',
        defaultMessage: 'Spa break',
        description: 'Category name for spa breaks in the hotel type filter or category carousel',
      }),
      imageId: 'hotel-category-tile-spa-break',
      show: true,
      parameterName: 'holidayTypes',
      parameterValue: 'Spa Break',
    },
    {
      id: 'romantic',
      name: intl.formatMessage({
        id: '09NMZw803R',
        defaultMessage: 'Romantic',
        description: 'Category name for romantic getaway properties in the hotel type filter carousel',
      }),
      imageId: 'hotel-category-tile-romantic-2',
      show: true,
      parameterName: 'holidayTypes',
      parameterValue: 'Romantic',
    },
    {
      id: 'homes_and_villas',
      name: intl.formatMessage({
        id: 'JsRicDWrr0',
        defaultMessage: 'Homes & Villas',
        description: 'Category name for homes and villas in the hotel type filter or category carousel',
      }),
      imageId: 'c9f8da1e-439a-4301-9a5d-0ec10be87ba7',
      show: true,
      parameterName: 'link',
      parameterValue: `/search/homes-and-villas?destinationId=${ANYWHERE_PLACE_ID}&destinationName=${ANYWHERE_NAME}&adults=2&children=none`,
    },
  ], [intl])

  return (
    <Root>
      <VerticalSpacer gap={20}>
        <LayoutContainer>
          <Heading variant="heading2">
            {headingText}
          </Heading>
        </LayoutContainer>
        <FullWidthCarousel snap="start" itemsPerArrow={6}>
          {categories.map(category => {
            return <HotelPageSearchHotelCategoryTile key={category.id} category={category} />
          })}
        </FullWidthCarousel>
      </VerticalSpacer>
    </Root>
  )
}

export default HotelPageDestinationTiles
